import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useIsAdmin } from './useAdmin'

// Types for stats data
export interface TimeseriesPoint {
  date: string
  count: number
}

export interface WeeklyTimeseriesPoint {
  weekStart: string
  count: number
}

export interface MonthlyTimeseriesPoint {
  monthStart: string
  count: number
}

export interface SessionTimeseriesPoint {
  date: string
  avgMinutes: number
  totalMinutes: number
}

export interface StatsData {
  users: {
    total: number
    byCountry: Record<string, number>
    timeseries: { date: string; cumulative: number }[]
  }
  activeUsers: {
    timeseries: TimeseriesPoint[]
  }
  weeklyActiveUsers: {
    timeseries: WeeklyTimeseriesPoint[]
  }
  monthlyActiveUsers: {
    timeseries: MonthlyTimeseriesPoint[]
  }
  maps: {
    total: number
    timeseries: TimeseriesPoint[]
  }
  flights: {
    total: number
    timeseries: TimeseriesPoint[]
  }
  signins: {
    timeseries: TimeseriesPoint[]
  }
  sessions: {
    avgMinutes: number
    timeseries: SessionTimeseriesPoint[]
  }
  visits: {
    dashboard: { total: number; timeseries: TimeseriesPoint[] }
    docs: { total: number; timeseries: TimeseriesPoint[] }
    vozillaSignins: { total: number; timeseries: TimeseriesPoint[] }
  }
  topline: {
    mapsThisWeek: number
    mapsLastWeek: number
    flightsThisWeek: number
    flightsLastWeek: number
  }
}

interface StatsResponse {
  success: boolean
  data?: StatsData
  error?: string
}

/**
 * Get Supabase Functions URL based on environment
 */
function getSupabaseFunctionsUrl(): string {
  const isDev = import.meta.env.MODE === 'development'
  const useProdSupabase = import.meta.env.VITE_USE_PROD_SUPABASE === 'true'
  const useDevConfig = isDev && !useProdSupabase

  const supabaseUrl = useDevConfig
    ? import.meta.env.VITE_DEV_SUPABASE_URL
    : import.meta.env.VITE_SUPABASE_URL

  return `${supabaseUrl}/functions/v1`
}

/**
 * Fetch admin statistics from edge function
 */
async function fetchAdminStats(): Promise<StatsData> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('Not authenticated')
  }

  const functionsUrl = getSupabaseFunctionsUrl()
  const response = await fetch(`${functionsUrl}/admin-stats`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  })

  const result: StatsResponse = await response.json()

  if (!response.ok || !result.success || !result.data) {
    throw new Error(result.error || 'Failed to fetch statistics')
  }

  return result.data
}

/**
 * Calculate sum for a time period (last N days)
 */
export function sumForPeriod(timeseries: TimeseriesPoint[], days: number): number {
  const slice = timeseries.slice(-days)
  return slice.reduce((sum, point) => sum + point.count, 0)
}

/**
 * Calculate percentage change vs previous period
 */
export function calculateChange(timeseries: TimeseriesPoint[], days: number): number {
  if (timeseries.length < days * 2) return 0
  
  const currentPeriod = timeseries.slice(-days)
  const previousPeriod = timeseries.slice(-days * 2, -days)
  
  const currentSum = currentPeriod.reduce((sum, p) => sum + p.count, 0)
  const previousSum = previousPeriod.reduce((sum, p) => sum + p.count, 0)
  
  if (previousSum === 0) return currentSum > 0 ? 100 : 0
  return Math.round(((currentSum - previousSum) / previousSum) * 100)
}

/**
 * Get the latest value from a cumulative timeseries
 */
export function getLatestCumulative(timeseries: { date: string; cumulative: number }[]): number {
  return timeseries.length > 0 ? timeseries[timeseries.length - 1].cumulative : 0
}

interface UseAdminStatsReturn {
  data: StatsData | undefined
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  error: Error | null
  refetch: () => Promise<unknown>
  dataUpdatedAt: number
}

/**
 * Hook to fetch admin statistics
 */
export function useAdminStats(): UseAdminStatsReturn {
  const { isAdmin, isLoading: adminLoading } = useIsAdmin()

  const query = useQuery<StatsData>({
    queryKey: ['admin', 'stats'],
    enabled: isAdmin && !adminLoading,
    queryFn: fetchAdminStats,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  })

  return {
    data: query.data,
    isLoading: query.isLoading || adminLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    dataUpdatedAt: query.dataUpdatedAt,
  }
}
